/**
 * Runtime environment configuration.
 *
 * On the client, reads from window.__ENV (injected at container startup).
 * On the server, reads from process.env directly.
 *
 * This allows a single Docker image to be deployed to any environment
 * by setting environment variables at runtime rather than build time.
 */

declare global {
  interface Window {
    __ENV?: Record<string, string>;
  }
}

const isBrowser = typeof window !== 'undefined';

/**
 * Build-time env values using static access patterns.
 * These are inlined by webpack's DefinePlugin at build time and serve as
 * a fallback when dynamic process.env[key] access doesn't have the values
 * (e.g., during next start in production where .env may not be loaded into process.env).
 */
const BUILD_TIME_ENV: Record<string, string | undefined> = {
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
  NEXT_PUBLIC_DEV_SUBDOMAIN: process.env.NEXT_PUBLIC_DEV_SUBDOMAIN,
  NEXT_PUBLIC_SENTRY_APP_DNS: process.env.NEXT_PUBLIC_SENTRY_APP_DNS,
  NEXT_PUBLIC_HEAP_ANALYTICS_APP_ID:
    process.env.NEXT_PUBLIC_HEAP_ANALYTICS_APP_ID,
  NEXT_PUBLIC_ADYEN_CLIENT_API_KEY:
    process.env.NEXT_PUBLIC_ADYEN_CLIENT_API_KEY,
  NEXT_PUBLIC_ADYEN_ENVIRONMENT: process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT,
  NEXT_PUBLIC_HAPPY_RETURNS_ID: process.env.NEXT_PUBLIC_HAPPY_RETURNS_ID,
};

/**
 * Get an environment variable at runtime.
 * Client-side: reads from window.__ENV (populated via _document.tsx), falls back to build-time values
 * Server-side: reads from process.env (dynamic), falls back to build-time inlined values
 */
export function getEnv(key: string): string | undefined {
  let value: string | undefined;

  if (isBrowser) {
    value = window.__ENV?.[key];
  } else {
    value = process.env[key];
  }

  // Fall back to build-time inlined value if runtime value is not available
  if (!value || value === 'RUNTIME_PLACEHOLDER') {
    const buildValue = BUILD_TIME_ENV[key];
    if (buildValue && buildValue !== 'RUNTIME_PLACEHOLDER') {
      value = buildValue;
    }
  }

  return value && value !== 'RUNTIME_PLACEHOLDER' ? value : undefined;
}

/**
 * Get an environment variable with a default fallback.
 */
export function getEnvOrDefault(key: string, defaultValue: string): string {
  return getEnv(key) ?? defaultValue;
}

/**
 * Safely serialize env config for injection into an inline <script> tag.
 * Escapes sequences that could break out of the script context (XSS prevention).
 */
export function safeSerialize(obj: Record<string, string>): string {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/** All runtime env var keys used by retailer-app */
export const ENV_KEYS = [
  'NEXT_PUBLIC_API_URL',
  'NEXT_PUBLIC_APP_ENV',
  'NEXT_PUBLIC_DEV_SUBDOMAIN',
  'NEXT_PUBLIC_SENTRY_APP_DNS',
  'NEXT_PUBLIC_HEAP_ANALYTICS_APP_ID',
  'NEXT_PUBLIC_ADYEN_CLIENT_API_KEY',
  'NEXT_PUBLIC_ADYEN_ENVIRONMENT',
  'NEXT_PUBLIC_HAPPY_RETURNS_ID',
] as const;
